/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { FlexLayout, FlexItem } from '@waca/ca-ui-toolkit';
import { FILTER_TYPE, FILTER_RANGE_TYPE, FILTER_RANGE_SUBTYPE } from './filterProps';

import FilterHeader from './FilterHeader';
import FilterFooter from './FilterFooter';
import DateTimeFilterPane from './DateTimeFilterPane';

export default class FullDateTimeFilter extends Component<any, any> {
	static propTypes = {
		title: PropTypes.string.isRequired,
		rangeType: PropTypes.string,
		rangeSubtype: PropTypes.string,
		dataType: PropTypes.string,
		fromValue: PropTypes.string,
		toValue: PropTypes.string,
		locale: PropTypes.string,
		timezone: PropTypes.string,
		includeNulls: PropTypes.bool,
		filterSelectionType: PropTypes.oneOf([FILTER_TYPE.RANGE, FILTER_TYPE.SELECT_VALUE]),
		onChange: PropTypes.func.isRequired,
		onApply: PropTypes.func.isRequired,
		onClear: PropTypes.func.isRequired,
		onClose: PropTypes.func.isRequired,
		onDone: PropTypes.func.isRequired,
		loading: PropTypes.bool,
		readOnly: PropTypes.bool,
		allowSearch: PropTypes.bool,
		allowSwitch: PropTypes.bool,
		allowIncludeNulls: PropTypes.bool,
		inverted: PropTypes.bool,
		selectedValuesCount: PropTypes.number,
		hasMoreData: PropTypes.bool,
		isDirty: PropTypes.bool,
	};

	static defaultProps = {
		dataType: 'datetime',
		rangeType: FILTER_RANGE_TYPE.BETWEEN,
		rangeSubtype: FILTER_RANGE_SUBTYPE.DATE_AND_TIME,
		fromValue: '',
		toValue: '',
		includeNulls: false,
		selectedValuesCount: 0,
		loading: false,
		showLabels: true,
		timezone: 'UTC',
		readOnly: false,
		allowSearch: true,
		allowSwitch: true,
		allowIncludeNulls: true,
		hasMoreData: false,
	};

	// States
	state = {
		isDirty: false,
		isValid: true,
		// these states are used to determine dirty
		// these values are set in _resetDirty()
		filterSelectionType: undefined,
		inverted: undefined,
		selectedValuesCount: undefined,
		rangeType: undefined,
		rangeSubtype: undefined,
		fromValue: undefined,
		toValue: undefined,
	};

	componentWillMount() {
		this._resetDirty();
	}

	componentWillReceiveProps(nextProps) {
		this.setState({
			isDirty:
				this.state.filterSelectionType !== nextProps.filterSelectionType ||
				this.state.inverted !== nextProps.inverted ||
				(this.state.filterSelectionType === FILTER_TYPE.RANGE &&
					(this.state.rangeType !== nextProps.rangeType ||
						this.state.rangeSubtype !== nextProps.rangeSubtype ||
						(!!this.state.fromValue && this.state.fromValue !== nextProps.fromValue) ||
						(!!this.state.toValue && this.state.toValue !== nextProps.toValue))) ||
				(this.state.filterSelectionType === FILTER_TYPE.SELECT_VALUE &&
					this.state.selectedValuesCount !== nextProps.selectedValuesCount),
		});
	}

	_resetDirty = () => {
		this.setState({
			isDirty: false,
			filterSelectionType: this.props.filterSelectionType,
			inverted: this.props.inverted,
			selectedValuesCount: this.props.selectedValuesCount,
			rangeType: this.props.rangeType,
			rangeSubtype: this.props.rangeSubtype,
			fromValue: this.props.fromValue,
			toValue: this.props.toValue,
		});
	};

	_setValidationStatus = isValid => this.setState({ isValid });
	_onApply = () => {
		this.props.onApply();
		this._resetDirty();
	};

	_onClear = () => {
		this.props.onClear();
	};

	_onOk = () => {
		this._onApply();
		this.props.onDone();
	};

	_onCancel = () => {
		this.props.onClose();
	};

	render() {
		const {
			title,
			rangeType,
			rangeSubtype,
			dataType,
			fromValue,
			toValue,
			includeNulls,
			locale,
			timezone,
			filterSelectionType,
			loading,
			readOnly,
			allowSearch,
			allowIncludeNulls,
			allowSwitch,
			onChange,
			hasMoreData,
		} = this.props;
		const showClose = true;

		return (
			<FlexLayout fullHeight direction="column" data-tid="fullDateTimeFilter">
				<FlexItem gutter={3}>
					<FilterHeader
						title={title}
						showSearch={false} // always false, this controls the icon
						showMenu={false}
						showClose={showClose}
						onClose={this.props.onClose}
						loading={loading}
						height={16}
						readOnly={readOnly}
					/>
				</FlexItem>
				<FlexItem grow margin={[0, 3, 0, 3]}>
					<DateTimeFilterPane
						{...this.props}
						rangeType={rangeType}
						rangeSubtype={rangeSubtype}
						dataType={dataType}
						fromValue={fromValue}
						toValue={toValue}
						includeNulls={includeNulls}
						locale={locale}
						timezone={timezone}
						allowSearch={allowSearch}
						filterSelectionType={filterSelectionType}
						onChange={onChange}
						setValidationStatus={this._setValidationStatus}
						loading={loading}
						filterType="full"
						readOnly={readOnly}
						allowSwitch={allowSwitch}
						allowIncludeNulls={allowIncludeNulls}
						hasMoreData={hasMoreData}
						isDirty={this.state.isDirty}
						isValid={this.state.isValid}
					/>
				</FlexItem>
				<FlexItem gutter={[2, 3, 3, 3]}>
					<FilterFooter
						loading={loading}
						readOnly={readOnly}
						onClear={this._onClear}
						onApply={this._onApply}
						onOk={this._onOk}
						onCancel={this._onCancel}
						dirty={
							(this.props.isDirty === true || this.props.isDirty === false
								? this.props.isDirty
								: this.state.isDirty) && this.state.isValid
						}
					/>
				</FlexItem>
			</FlexLayout>
		);
	}
}
